/**
 * Creating and editing a service, as a dialog.
 *
 * web_admin edits a service in an inline panel that pushes the list down the
 * page; here it is a `Modal`, because the list this form belongs to is a row you
 * were pointing at and shoving it out from under the pointer loses your place.
 *
 * The state, the validation and `POST/PATCH /admin/products` all live in the
 * shared `useProductForm` hook, so the two apps cannot disagree about what a
 * valid service is - only about what the fields look like.
 *
 * The dialog is wider than the default: `.crm-modal` sets `width: min(520px,
 * 100%)`, which is WIDTH and not max-width, so `max-w-[720px]` here would be
 * dead code and silently give 520. `w-[min(720px,100%)]` is the override that
 * bites.
 */
import type { FormEvent } from "react";
import { Loader2 } from "lucide-react";
import {
  EDIT_LOCALES,
  ServiceIcon,
  cn,
  makeT,
  useProductForm,
  type Locale,
  type ProductDTO,
} from "@wa/ui";
import { api } from "../../../../lib/api";
import { Btn, Modal } from "../../../ui";
import { Field, LangSwitch, LocaleField, Pill, deriveFilledMap } from "../_shared/LocaleFields";

/**
 * The fast path for the icon field, not a catalogue.
 *
 * The shared `IconPickerField` searches all ~1900 lucide names, but it does it
 * in an absolutely positioned popover, and `.crm-modal` is `overflow-y: auto` -
 * which makes it a scroll container on BOTH axes, so a panel that overflows the
 * dialog gets clipped rather than floating over it. Its chrome is web_admin's
 * too (bordered input, square card). A dozen inline suggestions plus the free
 * text field below cover what a service icon actually needs, with nothing to
 * clip and nothing to position.
 */
const SUGGESTED = [
  "server", "globe", "layers", "package", "rocket", "shield-check",
  "sparkles", "zap", "cloud", "database", "search", "megaphone",
  "pen-tool", "wrench", "life-buoy", "settings",
];

export function ProductFormModal({
  product,
  locale,
  onClose,
  onSaved,
}: {
  /** `null` = create a new service. */
  product: ProductDTO | null;
  locale: Locale;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = makeT(locale);
  const f = useProductForm({ api, locale, product, onSaved });
  const title = f.isNew ? t("products.newTitle") : t("products.editTitle");

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!f.canSave) return;
    void f.save();
  }

  return (
    <Modal title={title} label={title} onClose={onClose} className="w-[min(720px,100%)]">
      <form onSubmit={submit}>
        {/* Which language you are typing in, and which ones still have nothing in
            them - a filled circle means that locale has text. */}
        <div className="mt-5 flex justify-end">
          <LangSwitch
            active={f.activeLocale}
            onChange={f.setActiveLocale}
            locales={EDIT_LOCALES}
            filled={deriveFilledMap(EDIT_LOCALES, f.name, f.description)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* The code is the service's identity in the API and in every plan that
              points at it, so it is set once and never edited. */}
          {f.isNew ? (
            <div className="min-w-0">
              <Field label={t("editor.code")} hint={t("editor.codeHint")}>
                <Pill value={f.code} onChange={f.setCode} placeholder="hosting" />
              </Field>
            </div>
          ) : null}
          <div className="min-w-0">
            <Field label={t("editor.sortOrder")}>
              <Pill value={f.sortOrder} onChange={f.setSortOrder} type="number" inputMode="numeric" />
            </Field>
          </div>
        </div>

        <LocaleField
          label={t("editor.name")}
          required
          value={f.name}
          onChange={f.setName}
          activeLocale={f.activeLocale}
        />
        <LocaleField
          label={t("editor.desc")}
          as="textarea"
          rows={3}
          value={f.description}
          onChange={f.setDescription}
          activeLocale={f.activeLocale}
        />

        <Field label={t("products.field.icon")} hint={t("products.iconHint")}>
          <label className="crm-inp min-w-0">
            <ServiceIcon name={f.icon} className="size-4 shrink-0 text-primary" />
            <input
              value={f.icon}
              onChange={(e) => f.setIcon(e.target.value.trim())}
              placeholder="server"
              autoComplete="off"
            />
            {f.icon ? (
              <button
                type="button"
                onClick={() => f.setIcon("")}
                className="crm-mut shrink-0 text-[11.5px] font-semibold transition-colors hover:text-foreground"
              >
                {t("icon.clear")}
              </button>
            ) : null}
          </label>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {SUGGESTED.map((name) => (
              <button
                key={name}
                type="button"
                title={name}
                aria-label={name}
                aria-pressed={f.icon === name}
                onClick={() => f.setIcon(name)}
                className={cn(
                  "flex size-9 items-center justify-center rounded-full bg-[var(--crm-sunk)] transition-colors",
                  f.icon === name
                    ? "text-primary ring-2 ring-primary"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                <ServiceIcon name={name} className="size-4" />
              </button>
            ))}
          </div>
        </Field>

        {f.error ? <p className="crm-alert mt-4 px-4 py-2.5 text-[12.75px]">{f.error}</p> : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={f.saving}>
            {t("cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={!f.canSave}>
            {f.saving ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
